/** Formatting helpers shared by the admin hub and editor. */
import type { AdminProposalSummary } from './api';

const audFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  maximumFractionDigits: 0,
});

export function formatAud(dollars: number): string {
  return audFormatter.format(dollars);
}

/** Relative "time ago" for the hub's last-viewed / updated timestamps. */
export function relativeTime(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return 'never';
  const diff = Date.now() - ms;
  if (diff < 0) return 'just now';
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < minute) return 'just now';
  if (diff < hour) return `${Math.floor(diff / minute)}m ago`;
  if (diff < day) return `${Math.floor(diff / hour)}h ago`;
  if (diff < 30 * day) return `${Math.floor(diff / day)}d ago`;
  return new Date(ms).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatDate(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '';
  return new Date(ms).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Hub row's package summary: the recommended package's name + price when
 *  one is set, otherwise a price range (or the single price). */
export function packageSummary(proposal: Pick<AdminProposalSummary, 'packages' | 'recommended_package'>): string {
  const { packages, recommended_package: recommended } = proposal;
  if (packages.length === 0) return 'No packages yet';
  if (recommended) {
    const match = packages.find((pkg) => pkg.name === recommended);
    if (match) return `${match.name} · ${formatAud(match.price)}`;
  }
  const prices = packages.map((pkg) => pkg.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? formatAud(min) : `${formatAud(min)}–${formatAud(max)}`;
}

/** Hub row's view summary: "3 opens · last viewed 2d ago", or a quiet
 *  not-viewed-yet line that never reads as an error. */
export function viewsSummary(
  proposal: Pick<AdminProposalSummary, 'open_count' | 'last_viewed_at'>,
): string {
  const count = proposal.open_count ?? 0;
  if (count === 0) return 'Not viewed yet';
  const opens = `${count} open${count === 1 ? '' : 's'}`;
  return `${opens} · last viewed ${relativeTime(proposal.last_viewed_at)}`;
}
